export type PromiseStatus = 'fulfilled' | 'ongoing' | 'broken' | 'unknown';

export type PromiseSector = 
  | 'Infrastructure' 
  | 'Education' 
  | 'Health' 
  | 'Economy' 
  | 'Environment'
  | 'Agriculture'
  | 'Technology'
  | 'Foreign Policy'
  | 'Social Welfare'
  | 'Internal Affairs';

export interface Promise {
  id: string;
  title: string;
  politician: string;
  politicalParty: string;
  sector: PromiseSector;
  status: PromiseStatus;
  dateMade: string;
  deadline: string | null;
  description: string;
  sourceUrl: string;
  progress: string[];
}

export const promises: Promise[] = [
  {
    id: "construct-kathmandu-terai-fast-track",
    title: "Construct Kathmandu-Terai Fast Track",
    politician: "Pushpa Kamal Dahal",
    politicalParty: "CPN-Maoist Centre",
    sector: "Infrastructure",
    status: "ongoing",
    dateMade: "2022-07-15",
    deadline: "2025-12-31",
    description: "Promise to complete the 76.2 km expressway connecting Kathmandu to Nijgad in Bara district. This will reduce travel time from Kathmandu to Terai from 5 hours to 1.5 hours.",
    sourceUrl: "https://kathmandupost.com/national/2022/07/15/government-pledges-to-expedite-fast-track-project",
    progress: [
      "July 2022: Project restarted with new contractors",
      "March 2023: 35% of total work completed",
      "December 2023: Tunnel construction begins"
    ]
  },
  {
    id: "provide-free-education-secondary",
    title: "Provide free education up to secondary level",
    politician: "Sher Bahadur Deuba",
    politicalParty: "Nepali Congress",
    sector: "Education",
    status: "broken",
    dateMade: "2021-09-10",
    deadline: "2023-01-01",
    description: "Promise to make education free for all students up to secondary level (grade 12) in public schools across Nepal, including providing necessary textbooks and learning materials.",
    sourceUrl: "https://thehimalayantimes.com/nepal/government-to-provide-free-education-up-to-grade-12",
    progress: [
      "September 2021: Policy announced",
      "January 2022: Budget allocated for free textbooks",
      "June 2022: Implementation in all provinces",
      "January 2023: Full implementation completed"
    ]
  },
  {
    id: "double-agricultural-production",
    title: "Double agricultural production in 5 years",
    politician: "KP Sharma Oli",
    politicalParty: "CPN-UML",
    sector: "Agriculture",
    status: "broken",
    dateMade: "2019-02-15",
    deadline: "2024-02-15",
    description: "Promise to double Nepal's agricultural production within 5 years through modernization, irrigation projects, and farmer subsidies.",
    sourceUrl: "https://myrepublica.nagariknetwork.com/news/government-aims-to-double-agricultural-production-in-five-years",
    progress: [
      "February 2019: Agricultural Modernization Project launched",
      "August 2020: Project halted due to funding issues",
      "March 2021: Program officially discontinued",
      "June 2022: New government confirms targets will not be met"
    ]
  },
  {
    id: "universal-health-coverage",
    title: "Universal health coverage for all citizens",
    politician: "Bidhya Devi Bhandari",
    politicalParty: "CPN-UML",
    sector: "Health",
    status: "ongoing",
    dateMade: "2021-05-20",
    deadline: "2026-05-20",
    description: "Commitment to implement universal health coverage ensuring all Nepali citizens have access to essential health services without financial hardship.",
    sourceUrl: "https://kathmandupost.com/health/2021/05/20/nepal-commits-to-universal-health-coverage",
    progress: [
      "May 2021: Universal Health Coverage Act passed",
      "January 2022: Pilot program in three districts",
      "November 2023: Expanded to 25 districts",
      "Current coverage: 45% of population"
    ]
  },
  {
    id: "establish-it-park-province",
    title: "Establish IT park in each province",
    politician: "Gokarna Bista",
    politicalParty: "CPN-UML",
    sector: "Technology",
    status: "unknown",
    dateMade: "2020-11-10",
    deadline: "2024-12-31",
    description: "Promise to establish a fully-equipped IT park in each of Nepal's seven provinces to boost the technology sector and create employment for youth.",
    sourceUrl: "https://techlekh.com/it-park-every-province-nepal/",
    progress: [
      "November 2020: Feasibility study launched",
      "March 2021: Land acquired in Province 1",
      "No further updates available since change in government"
    ]
  },
  {
    id: "connect-villages-paved-roads",
    title: "Connect all villages with paved roads",
    politician: "Pushpa Kamal Dahal",
    politicalParty: "CPN-Maoist Centre",
    sector: "Infrastructure",
    status: "ongoing",
    dateMade: "2022-01-15",
    deadline: "2027-01-15",
    description: "Promise to connect all villages across Nepal with paved roads to improve rural connectivity and economic opportunities.",
    sourceUrl: "https://www.nepalitimes.com/latest/nepal-aims-for-full-road-connectivity-in-rural-areas/",
    progress: [
      "January 2022: Rural Connectivity Project launched",
      "September 2022: 120 villages connected",
      "June 2023: 350 more villages connected",
      "Current progress: 28% complete"
    ]
  },
  {
    id: "end-load-shedding",
    title: "End load shedding completely",
    politician: "Kulman Ghising",
    politicalParty: "Independent (NEA)",
    sector: "Infrastructure",
    status: "fulfilled",
    dateMade: "2016-09-14",
    deadline: "2018-05-01",
    description: "Promise to end the persistent power cuts (load shedding) that had plagued Nepal for over a decade by improving electricity generation and distribution.",
    sourceUrl: "https://kathmandupost.com/money/2016/09/26/ghising-promises-to-end-load-shedding",
    progress: [
      "September 2016: Management reforms at Nepal Electricity Authority",
      "December 2016: Load shedding reduced to 6 hours daily",
      "April 2017: Load shedding in Kathmandu Valley eliminated",
      "May 2018: Load shedding nationwide eliminated"
    ]
  },
  {
    id: "plant-fifty-million-trees",
    title: "Plant 50 million trees in one year",
    politician: "Ram Chandra Poudel",
    politicalParty: "Nepali Congress",
    sector: "Environment",
    status: "broken",
    dateMade: "2023-03-10",
    deadline: "2024-03-10",
    description: "Ambitious promise to plant 50 million trees across Nepal within one year to combat deforestation and climate change impacts.",
    sourceUrl: "https://www.spotlightnepal.com/2023/03/nepal-launches-ambitious-tree-planting-campaign/",
    progress: [
      "March 2023: Campaign launched",
      "June 2023: 5 million trees planted",
      "October 2023: Project funding cut",
      "February 2024: Official announcement that target won't be met",
      "Final count: Only 8.6 million trees planted"
    ]
  },
  {
    id: "address-systemic-issues-policy",
    title: "Address and fix systemic issues at the policy level to prevent political parties from shielding perpetrators and causing mismanagement of cooperative funds",
    politician: "Swarnim Wagle",
    politicalParty: "National Independent Party (RSP)",
    sector: "Internal Affairs",
    status: "ongoing",
    dateMade: "2024-09-16",
    deadline: null,
    description: "Promise to implement policy reforms to prevent political interference in cooperative fund management and ensure accountability for perpetrators of financial mismanagement.",
    sourceUrl: "https://x.com/nepalkhabar/status/1835644714785386547",
    progress: [
      "September 2024: Policy reform initiative announced"
    ]
  },
];
